import React, { useState, useEffect } from 'react';
import { X, Upload, Download, CheckCircle, FileUp, FileSpreadsheet } from 'lucide-react';
import { toast } from 'react-toastify';
import { clientsAPI } from '../../../services/api';

// ── Fields that should never appear in the template selector
const HIDDEN_FIELDS = new Set([
  'id',
  'organizationId',
  'createdBy',
  'createdAt',
  'updatedAt',
  'lastActivity',
  'contactIds',
  'isShared',
  'assignedTo',
  'migratedFromCompanyId',
  'migratedAt',
  'clientCompanyLogoUrl',
  'primaryAgreementUrl',
  'workOrderUrl',
  'supportingDocumentUrls',
]);

// ── Always selected, cannot be unchecked
const REQUIRED_FIELDS = ['companyName', 'email', 'primaryContact'];

const ImportClientsModal = ({ onClose, onImport }) => {
  const [mode, setMode] = useState(null);       // null | 'template' | 'direct'
  const [step, setStep] = useState(1);          // template flow: 1 = pick fields, 2 = upload

  const [columns, setColumns] = useState([]);
  const [selectedFields, setSelectedFields] = useState(REQUIRED_FIELDS);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    fetchColumns();
  }, []);

  const fetchColumns = async () => {
    setLoading(true);
    try {
      const response = await clientsAPI.getAllColumns();
      const formatted = (response.data || [])
        .filter((col) => !HIDDEN_FIELDS.has(col))
        .map((col) => ({
          value: col,
          label: col
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .replace(/^./, (s) => s.toUpperCase()),
        }));
      setColumns(formatted);
    } catch (error) {
      console.error('Error fetching columns:', error);
      toast.error('Failed to fetch columns');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (field) => {
    if (REQUIRED_FIELDS.includes(field)) return; // lock required fields
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  /* ── Generate & download template ── */
  const handleGenerateTemplate = async () => {
    if (selectedFields.length === 0) {
      toast.warning('Please select at least one field');
      return;
    }
    setLoading(true);
    try {
      const response = await clientsAPI.generateTemplate(selectedFields);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'client_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded! Fill it in and upload below.');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate template');
    } finally {
      setLoading(false);
    }
  };

  /* ── File picker ── */
  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const valid = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!valid.includes(selected.type)) {
      toast.error('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }
    setFile(selected);
    setImportResult(null);
  };

  /* ── Import ── */
  const handleImport = async () => {
    if (!file) {
      toast.warning('Please select a file to import');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await clientsAPI.importClients(formData);
      const data = response.data;
      setImportResult(data);

      const { successCount = 0, failedCount = 0, totalRecords = 0 } = data;

      if (successCount > 0 && failedCount === 0) {
        toast.success(`✅ All ${successCount} client(s) imported successfully!`);
      } else if (successCount > 0 && failedCount > 0) {
        toast.success(`✅ ${successCount} of ${totalRecords} client(s) imported successfully`);
        toast.warning(`⚠️ ${failedCount} record(s) failed — see details below`);
      } else if (successCount === 0 && failedCount > 0) {
        toast.error(`❌ Import failed — ${failedCount} record(s) had errors`);
      }

      if (successCount > 0) {
        setTimeout(() => onImport?.(), 500);
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to import clients';
      toast.error(`❌ ${msg}`);
      setImportResult({ successCount: 0, failedCount: 0, errors: [{ reason: msg }] });
    } finally {
      setLoading(false);
    }
  };

  const downloadErrorCSV = () => {
    const errors = importResult?.errors || [];
    const header = 'Row,Company / Email,Reason';
    const rows = errors.map(e => {
      const identifier = e.companyName || e.email || '';
      return [String(e.row ?? ''), identifier, e.reason || '']
        .map(v => `"${String(v).replace(/"/g, '""')}"`)
        .join(',');
    });
    const csv = [header, ...rows].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'client_import_errors.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  /* ── Shared upload zone ── */
  const UploadZone = () => (
    <div className="space-y-4">
      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          {mode === 'template' ? 'Step 2: Upload Filled Template' : 'Upload Excel File'}
        </h3>
        <p className="text-sm text-gray-600">
          {mode === 'template'
            ? 'Fill the downloaded template with your client data and upload it here.'
            : 'Upload your existing Excel file with client data.'}
        </p>
      </div>

      <label className="block border-2 border-dashed border-gray-300 rounded-lg p-10 text-center hover:border-blue-400 transition cursor-pointer">
        <Upload size={48} className="text-gray-400 mx-auto mb-3" />
        <span className="text-base text-blue-600 hover:text-blue-700 font-semibold">
          Click to choose file
        </span>
        <p className="text-sm text-gray-500 mt-1">or drag and drop your Excel file here</p>
        <p className="text-xs text-gray-400 mt-1">Supported formats: .xlsx, .xls</p>
        <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" />
      </label>

      {file && (
        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-green-900 truncate">{file.name}</p>
            <p className="text-xs text-green-700">
              {(file.size / 1024).toFixed(2)} KB · Ready to import
            </p>
          </div>
          <button onClick={() => setFile(null)} className="text-gray-400 hover:text-red-500 transition">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Import Result */}
      {importResult && (
        <div className={`rounded-lg p-4 border ${
          importResult.failedCount === 0
            ? 'bg-green-50 border-green-200'
            : importResult.successCount === 0
            ? 'bg-red-50 border-red-200'
            : 'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="space-y-2">
            {importResult.successCount > 0 && (
              <p className="text-sm font-medium text-green-800">
                ✅ Successfully imported: {importResult.successCount} client(s)
              </p>
            )}
            {importResult.failedCount > 0 && (
              <p className="text-sm font-medium text-red-800">
                ❌ Failed: {importResult.failedCount} record(s)
              </p>
            )}
            {importResult.errors?.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-700">
                    Error details ({importResult.errors.length} row{importResult.errors.length !== 1 ? 's' : ''})
                  </p>
                  <button
                    onClick={downloadErrorCSV}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded px-2.5 py-1.5 hover:bg-gray-50 transition"
                  >
                    <Download size={13} />
                    Download error report
                  </button>
                </div>
                <div className="max-h-52 overflow-y-auto border border-red-200 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-red-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-red-700 w-12">Row</th>
                        <th className="px-3 py-2 text-left font-semibold text-red-700">Company / Email</th>
                        <th className="px-3 py-2 text-left font-semibold text-red-700">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-100">
                      {importResult.errors.map((err, i) => (
                        <tr key={i} className="bg-white hover:bg-red-50">
                          <td className="px-3 py-2 font-semibold text-red-700">{err.row || '—'}</td>
                          <td className="px-3 py-2 text-gray-700">
                            {err.companyName || err.email || '—'}
                          </td>
                          <td className="px-3 py-2 text-red-600">{err.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} />

      <div
        className="relative bg-white shadow-2xl w-full max-w-3xl h-full flex flex-col overflow-hidden"
        style={{ animation: 'slideInRight 0.3s ease-out' }}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b bg-gradient-to-r from-[#1C4E6D] to-[#2a6a9f] text-white">
          <div>
            <h2 className="text-xl font-semibold">Import Clients</h2>
            <p className="text-sm text-blue-100 mt-0.5">Upload clients in bulk using Excel</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition">
            <X size={22} />
          </button>
        </div>

        {/* ── Mode chooser ── */}
        {mode === null && (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 py-10">
            <p className="text-base font-semibold text-gray-700">How would you like to import?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 w-full max-w-xl">

              <button
                onClick={() => { setMode('template'); setStep(1); }}
                className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-xl hover:border-[#1C4E6D] hover:bg-[#e8f4f9] transition"
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileSpreadsheet size={22} className="text-[#1C4E6D]" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Generate Template</p>
                  <p className="text-xs text-gray-500 mt-1">Download template and upload later</p>
                </div>
              </button>

              <button
                onClick={() => setMode('direct')}
                className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-xl hover:border-[#1C4E6D] hover:bg-[#e8f4f9] transition"
              >
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <FileUp size={22} className="text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Direct Upload</p>
                  <p className="text-xs text-gray-500 mt-1">Upload existing Excel file directly</p>
                </div>
              </button>

            </div>
          </div>
        )}

        {/* ── Template mode ── */}
        {mode === 'template' && (
          <div className="flex-1 overflow-y-auto px-6 py-6">

            {/* Step 1 — field selection */}
            {step === 1 && (
              <div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    Select Fields for Template
                  </h3>
                  <p className="text-sm text-gray-600">
                    Company Name, Email, and Primary Contact are mandatory and always included.
                  </p>
                </div>

                <div className="mb-4 flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">{selectedFields.length}</span> fields selected
                  </p>
                  <div className="flex gap-2 text-xs">
                    <button
                      onClick={() =>
                        setSelectedFields([
                          ...new Set([...REQUIRED_FIELDS, ...columns.map((c) => c.value)]),
                        ])
                      }
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Select All
                    </button>
                    <span className="text-gray-400">|</span>
                    <button
                      onClick={() => setSelectedFields(REQUIRED_FIELDS)}
                      className="text-gray-600 hover:text-gray-700 font-medium"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {columns.map((col) => (
                    <label
                      key={col.value}
                      className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition"
                    >
                      <input
                        type="checkbox"
                        checked={selectedFields.includes(col.value)}
                        disabled={REQUIRED_FIELDS.includes(col.value)}
                        onChange={() => handleCheckboxChange(col.value)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-gray-800 text-sm font-medium">{col.label}</span>
                        {REQUIRED_FIELDS.includes(col.value) && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">
                            Required
                          </span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleGenerateTemplate}
                    disabled={loading || selectedFields.length === 0}
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Generating...' : 'Generate Template'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 — upload */}
            {step === 2 && (
              <>
                <UploadZone />
                <div className="flex justify-between mt-6">
                  <button
                    onClick={() => { setStep(1); setFile(null); setImportResult(null); }}
                    className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={loading || !file}
                    className="px-5 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? 'Importing...' : 'Import Clients'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Direct upload mode ── */}
        {mode === 'direct' && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <UploadZone />
            </div>
            <div className="flex justify-between items-center gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => { setMode(null); setFile(null); setImportResult(null); }}
                className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm"
              >
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={loading || !file}
                className="px-5 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Importing...' : 'Import Clients'}
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default ImportClientsModal;